import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	Typography,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
} from '@mui/material';

import { api } from 'common/frontend/api';
import { DeliveryOptionWithProducts } from 'common/types';
import { moveItem, assignOrderIndexes } from 'common/utils/arrays';
import DeliveryOptionsList from 'components/DeliveryOptionsList';
import { useTranslation } from 'services/localization';
import * as NotificationActions from 'actions/NotificationActions';
import { BatchManager } from 'common/frontend/firebase/firestore';

interface Props {
	open: boolean;
	onClose: () => void;
	deliveryOptions: DeliveryOptionWithProducts[];
}

const DeliveryOptionsReorderDialog = (props: Props) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { open, onClose, deliveryOptions } = props;

	const [loading, setLoading] = useState<boolean>(false);
	const [reorderedDeliveryOptions, setReorderedDeliveryOptions] = useState<
		DeliveryOptionWithProducts[]
	>(deliveryOptions);

	const handleClose = () => {
		setReorderedDeliveryOptions(reorderedDeliveryOptions);
		onClose();
	};

	const updateDeliveryOptionOrderIndexes = async (
		options: (DeliveryOptionWithProducts & {
			orderIndex: number;
		})[],
	) => {
		const batchManager = new BatchManager();
		options.forEach((option) => {
			api(batchManager.batch()).deliveryOptions.doc(option.id).update({
				orderIndex: option.orderIndex,
			});
		});
		await batchManager.commit();
	};

	const handleSave = async () => {
		setLoading(true);
		try {
			//20.9.2022 Old delivery options dont have an order index (assigned as Infinity in dataUpdater) so we force the property
			const deliveryOptionsToUpdate = assignOrderIndexes(reorderedDeliveryOptions);
			if (deliveryOptionsToUpdate.length > 0) {
				await updateDeliveryOptionOrderIndexes(deliveryOptionsToUpdate);
			}
			handleClose();
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
		setLoading(false);
	};

	const handleMoveUp = (categoryId: string, currentIndex: number) => {
		const prevItem = reorderedDeliveryOptions[currentIndex - 1];
		if (!prevItem) return;

		const itemIndex = reorderedDeliveryOptions.findIndex((item) => item.id === categoryId);
		const targetIndex = reorderedDeliveryOptions.findIndex((item) => item.id === prevItem.id);

		if (itemIndex === -1 || targetIndex === -1) return;

		const result = moveItem(reorderedDeliveryOptions, itemIndex, targetIndex, 'before');
		setReorderedDeliveryOptions(result);
	};

	const handleMoveDown = (categoryId: string, currentIndex: number) => {
		const nextItem = reorderedDeliveryOptions[currentIndex + 1];
		if (!nextItem) return;
		const itemIndex = reorderedDeliveryOptions.findIndex((item) => item.id === categoryId);
		const targetIndex = reorderedDeliveryOptions.findIndex((item) => item.id === nextItem.id);
		if (itemIndex === -1 || targetIndex === -1) return;

		const result = moveItem(reorderedDeliveryOptions, itemIndex, targetIndex, 'after');
		setReorderedDeliveryOptions(result);
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Typography variant="h6" component="div">
					{t('DeliveryOptionsReorderDialog.reorderDeliveryOptions', 'Reorder delivery options')}
				</Typography>
				<Typography variant="body2" component="div">
					{t(
						'DeliveryOptionsReorderDialog.reorderDeliveryOptionsDescription',
						'Change the order in which your delivery options are displayed in your sales channels',
					)}
				</Typography>
			</DialogTitle>
			<DialogContent sx={{ minHeight: 200 }}>
				<DeliveryOptionsList
					deliveryOptions={reorderedDeliveryOptions}
					onMoveUp={handleMoveUp}
					onMoveDown={handleMoveDown}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>{t('common:actions.cancel')}</Button>
				<Button onClick={handleSave} color="primary" variant="contained">
					{loading ? <CircularProgress size={20} color="inherit" /> : t('common:actions.save')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DeliveryOptionsReorderDialog;
